import React, { useContext } from "react";
import { observer } from "mobx-react";
import { Plus } from "lucide-react";
import { AppWorkspaceContext } from "../../stores/AppWorkspace";
import {
  APP_PAGES,
  formatTimeLeft,
  assetName,
  coinFromAssetCode
} from "../../helpers/constants";
import { iconForSymbol } from "../../helpers/cryptoIcons";
import { Alert, Button, PageHeader } from "../../../ui";
import OrderDetail from "../OrderDetail";
import SwapTokenCallout from "../SwapTokenCallout";

const SwapsPage: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext);
  const orders = workspace.swapOrders;
  const viewed = workspace.viewedOrder;
  const notFound = workspace.publicToken != null && !viewed;

  // Belt and braces: the container already gates this page, and the server
  // sends no history without an API key. If we somehow render anyway, say why
  // rather than showing a misleadingly empty table.
  if (!workspace.canListSwaps) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Swap history" />
        <Alert tone="muted" title="An API key is required">
          <p className="text-sm">
            Swap history is available to accounts with an API key. You can still
            open any single swap using the swap token you saved when you created it.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {!workspace.isGuest && (
              <Button size="xs" onClick={() => workspace.setPage(APP_PAGES.API_TOKENS)}>
                Create an API key
              </Button>
            )}
            <Button
              size="xs"
              variant="secondary"
              onClick={() => workspace.setPage(APP_PAGES.FIND_SWAP)}
            >
              Find a swap by token
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  // The token is the only way back to a swap — nothing else records it.
  const saveLinkBanner = viewed && <SwapTokenCallout order={viewed} />;

  if (notFound) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Swap not found" />
        <Alert tone="muted">
          That link doesn’t match a swap. Check you copied all of it — the link
          is the only handle on a swap, so a partial one can’t be recovered.
        </Alert>
        <Button
          leftIcon={<Plus size={16} />}
          onClick={() => workspace.setPage(APP_PAGES.NEW_SWAP)}
          className="self-start"
        >
          Start a new swap
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {saveLinkBanner}
      <PageHeader
        title={viewed ? "Your swap" : "Swaps"}
        subtitle="Coin → Lightning orders. Live updates over WebSocket."
        actions={
          <Button leftIcon={<Plus size={16} />} onClick={() => workspace.setPage(APP_PAGES.NEW_SWAP)}>
            New swap
          </Button>
        }
      />

      <div className="overflow-x-auto">
        <table className="table-ls">
          <thead>
            <tr>
              <th>Order</th>
              <th>Pay in</th>
              <th>Receive</th>
              <th>Deposit</th>
              <th>Phase</th>
              <th>Expires</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={6} className="!py-8 text-center text-muted">
                  No swaps yet — create one to get a deposit address.
                </td>
              </tr>
            ) : (
              orders.map((o) => (
                <tr
                  key={o.id}
                  className={`cursor-pointer ${
                    workspace.selectedOrderId === o.id ? "table-ls-row-active" : ""
                  }`}
                  onClick={() => workspace.selectOrder(o.id)}
                >
                  <td className="font-mono text-sm font-semibold">{o.providerOrderId}</td>
                  <td>
                    <span className="inline-flex items-center gap-1.5">
                      <img
                        src={iconForSymbol(coinFromAssetCode(o.fromCode))}
                        alt=""
                        width={18}
                        height={18}
                      />
                      <span>{assetName(o.fromCode)}</span>
                    </span>
                  </td>
                  <td>{o.amountSats.toLocaleString()} sats</td>
                  <td className="font-mono">{o.depositAmount}</td>
                  <td className="font-semibold">{o.phaseLabel}</td>
                  <td className={o.timeLeft > 0 ? "" : "text-muted"}>
                    {formatTimeLeft(o.timeLeft)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {workspace.selectedOrder && <OrderDetail order={workspace.selectedOrder} />}
    </div>
  );
});

export default SwapsPage;
